<form [formGroup]="generalInformationForm">
  <mat-card class="detail-card" valumizeCardContextMenu [commentSlug]="'asset-general-information'">
    <mat-card-header class="detail-card-header">
      <mat-card-title data-test="title">General Information</mat-card-title>
    </mat-card-header>
    <mat-card-subtitle>
      @if ((gptGeneralInfo$ | async)?.status === 'ERROR') {
        <valumize-info-panel [appearance]="'ERROR'" [message]="gptErrorMessage"></valumize-info-panel>
      }
    </mat-card-subtitle>
    @if (asset$ | async; as asset) {
      <mat-card-content class="detail-card-content">
        <div class="general-information-form">
          <div class="key-value-pair-section">
            <div class="key-value-pair-column">
              <valumize-mergeable-select-multi-code
                [label]="'Region'"
                [isEditable]=isEditable
                [isDataset]=isDataset
                [codeTable]=codeTableRegion
                [codes]=asset.data.regions
                [comparativeCodes]=baselineAsset.regions
                [control]=generalInformationForm.controls.regions>
              </valumize-mergeable-select-multi-code>
              <valumize-mergeable-select-code
                [label]="'Country'"
                [isEditable]=isEditable
                [isDataset]=isDataset
                [codeTable]=codeTableCountry
                [code]=asset.data.country
                [baselineCode]=baselineAsset.country
                [control]=generalInformationForm.controls.country
                [searchable]=true>
              </valumize-mergeable-select-code>
              <valumize-mergeable-input
                [label]="'Activity Description'"
                [isEditable]=isEditable
                [isDataset]=isDataset
                [value]=asset.data.activityDescription
                [baselineValue]=baselineAsset.activityDescription
                [softLimit]=activityDescriptionSoftLimit
                [control]=generalInformationForm.controls.activityDescription>
              </valumize-mergeable-input>
              <valumize-mergeable-select-code
                [label]="'Stage'"
                [isEditable]=isEditable
                [isDataset]=isDataset
                [codeTable]=codeTableStage
                [code]=asset.data.stage
                [baselineCode]=baselineAsset.stage
                [control]=generalInformationForm.controls.stage>
              </valumize-mergeable-select-code>
              <div class="key-value-pair">
                <div class="key-value-item">GIC</div>
                <div class="key-value-item gic-value-container">
                  <span>{{gicDisplayValue}}</span>
                  @if (isEditable) {
                    <button type="button" mat-stroked-button color="primary" (click)="openGicsDialog()" class="gic-edit-button">
                      Select
                    </button>
                  }
                </div>
              </div>
            </div>
            <div class="key-value-pair-column">
              <valumize-mergeable-select-code
                [label]="'Currency'"
                [isEditable]=isEditable
                [isDataset]=isDataset
                [codeTable]=codeTableCurrency
                [code]=asset.data.currencyIso
                [baselineCode]=baselineAsset.currencyIso
                [control]=generalInformationForm.controls.currency
                [searchable]=true
                [showCode]="true">
              </valumize-mergeable-select-code>
              <valumize-mergeable-select-code
                [label]="'ESG'"
                [isEditable]=isEditable
                [isDataset]=isDataset
                [codeTable]=codeTableESG
                [code]=asset.data.esg
                [baselineCode]=baselineAsset.esg
                [control]=generalInformationForm.controls.esg>
              </valumize-mergeable-select-code>
              <valumize-mergeable-select-code
                [label]="'Assessment'"
                [isEditable]=isEditable
                [isDataset]=isDataset
                [codeTable]=codeTableAssessment
                [code]=asset.data.assessment
                [baselineCode]=baselineAsset.assessment
                [control]=generalInformationForm.controls.assessment>
              </valumize-mergeable-select-code>
              <valumize-mergeable-select-code
                [label]="'FY End'"
                [isEditable]=isEditable
                [isDataset]=isDataset
                [codeTable]=codeTableFiscalYearEnd
                [code]=asset.data.fiscalYearEnd
                [baselineCode]=baselineAsset.fiscalYearEnd
                [control]=generalInformationForm.controls.fiscalYearEnd>
              </valumize-mergeable-select-code>
              <valumize-mergeable-checkbox
                [label]="'Publicly Listed'"
                [isEditable]=isEditable
                [isDataset]=isDataset
                [value]=asset.data.publiclyListed
                [baselineValue]=baselineAsset.publiclyListed
                [control]=generalInformationForm.controls.publiclyListed>
              </valumize-mergeable-checkbox>
            </div>
          </div>
          @if (gptGeneralInfo$ | async; as gptGeneralInfo) {
            @if (isGptEditMode && gptGeneralInfo.status === 'LOADED' && gptGeneralInfo.data) {
              <div class="gpt-preview-section">
                <div class="gpt-preview-header">
                  <span>Generated Information (click Apply to use)</span>
                  <button mat-stroked-button color="primary" (click)="applyGeneratedInfo(gptGeneralInfo.data)">Apply</button>
                </div>
                <div class="gpt-preview-content">
                  <div class="gpt-preview-item"><strong>Regions:</strong> {{ formatRegions(gptGeneralInfo.data.regions) }}</div>
                  <div class="gpt-preview-item"><strong>Country:</strong> {{ gptGeneralInfo.data.country.code }}</div>
                  <div class="gpt-preview-item"><strong>Activity:</strong> {{ gptGeneralInfo.data.activityDescription.text }}</div>
                  <div class="gpt-preview-item"><strong>Stage:</strong> {{ gptGeneralInfo.data.stage.code }}</div>
                  <div class="gpt-preview-item"><strong>GIC:</strong> {{ gptGeneralInfo.data.gic.code }}</div>
                  <div class="gpt-preview-item"><strong>Currency:</strong> {{ gptGeneralInfo.data.currency.code }}</div>
                  <div class="gpt-preview-item"><strong>Publicly Listed:</strong> {{ gptGeneralInfo.data.publiclyListed }}</div>
                </div>
              </div>
            }
          }
        </div>
      </mat-card-content>
      <mat-card-actions class="detail-card-actions">
        @if ((gptGeneralInfo$ | async)?.status === 'LOADING') {
          <mat-spinner diameter="24"></mat-spinner>
        }
        @if (isEditable) {
          <button mat-icon-button
            (click)="isGptEditMode ? gptCancel() : gptEditMode()"
            class="image-button"
            aria-label="Generate Information">
            <img src="assets/logo.svg" alt="Valumize AI" />
          </button>
        }
        @if (!isEditable) {
          <button (click)="editMode()"
            [disabled]="(isEditDisabled$ | async)!"
            color="primary"
            data-test="edit-button"
            mat-icon-button>
            <mat-icon>edit</mat-icon>
          </button>
        }
        @if (isEditable) {
          <button (click)="save(); gptCancel()"
            [disabled]="generalInformationForm.invalid"
            color="primary"
            data-test="save-button"
            mat-icon-button>
            <mat-icon>save</mat-icon>
          </button>
        }
        @if (isEditable) {
          <button (click)="cancel(); gptCancel()"
            color="primary"
            data-test="cancel-button"
            mat-icon-button>
            <mat-icon>cancel</mat-icon>
          </button>
        }
        <valumize-card-comments-button [commentSlug]="'asset-general-information'"></valumize-card-comments-button>
      </mat-card-actions>
    }
  </mat-card>
</form>
